import { useCallback, useEffect, useMemo, useState } from "react";
import { ProjectOverviewLink } from "@/components/ProjectOverviewLink";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Checkbox, TextInput } from "@/components/ui/Field";
import { Spinner } from "@/components/ui/Spinner";
import { Table, type Column } from "@/components/ui/Table";
import { ApiError } from "@/lib/apiClient";
import { useCurrentUser, userIsManagement } from "@/lib/auth";
import { currentYear, shiftYear, yearDateRange } from "@/lib/auswertungen";
import {
  estimateTotal,
  fetchOutstandingBalancesReport,
  formatEuro,
  formatHoursDe,
  loadOpenHourRate,
  saveOpenHourRate,
  type OutstandingBalancesReport as OutstandingBalancesReportData,
  type OutstandingHoursItem,
  type OutstandingMaterialItem,
  type OutstandingOpenHoursItem,
  type OutstandingTravelItem,
} from "@/lib/outstandingBalances";
import { useRealtime } from "@/lib/useRealtime";
import { OutstandingMaterialKalkDialog } from "@/pages/auswertungen/OutstandingMaterialKalkDialog";
import { timesheetStatusLabel } from "@/pages/stundennachweise/timesheetFilterLogic";
import "./auswertungen.css";

function timesheetLabel(number: number | string | null | undefined, id: number): string {
  if (number != null && String(number).trim()) return `#${String(number).trim()}`;
  return `ID ${id}`;
}

export function OutstandingBalancesReport() {
  const { user } = useCurrentUser();
  const canKalkulateMaterial = userIsManagement(user);
  const [year, setYear] = useState(currentYear);
  const [hideFullLicense, setHideFullLicense] = useState(true);
  const [openHourRate, setOpenHourRate] = useState(() => loadOpenHourRate());
  const [rateInput, setRateInput] = useState(() => String(loadOpenHourRate()).replace(".", ","));
  const [report, setReport] = useState<OutstandingBalancesReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [kalkMaterial, setKalkMaterial] = useState<OutstandingMaterialItem | null>(null);

  const { from, to } = useMemo(() => yearDateRange(year), [year]);

  const load = useCallback(
    async (opts?: { background?: boolean }) => {
      if (opts?.background) setRefreshing(true);
      else setLoading(true);
      try {
        const data = await fetchOutstandingBalancesReport(from, to, { hideFullLicense });
        setReport(data);
        setError(null);
      } catch (err) {
        setError(
          err instanceof ApiError
            ? err.message
            : "Außenstände konnten nicht geladen werden.",
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [from, to, hideFullLicense],
  );

  useEffect(() => {
    void load();
  }, [load]);

  useRealtime({
    scopes: ["domain:timesheets"],
    domains: ["timesheets"],
    events: ["timesheet_status_changed", "timesheet_hero_usage_updated", "timesheet_updated"],
    onChange: () => void load({ background: true }),
  });

  const applyRate = useCallback(() => {
    const parsed = Number(String(rateInput).replace(",", "."));
    const next = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
    setOpenHourRate(next);
    saveOpenHourRate(next);
    setRateInput(String(next).replace(".", ","));
  }, [rateInput]);

  const summary = report?.summary;
  const estimatedTotal = estimateTotal(summary, openHourRate);
  const ohneProjektHours = summary?.ohne_projekt_hours ?? 0;
  const ohneProjektAmount =
    Number.isFinite(openHourRate) && summary
      ? ((summary.ohne_projekt_seconds ?? 0) / 3600) * openHourRate
      : 0;
  const ohneNachweisAmount = summary?.ohne_nachweis_amount ?? 0;

  const hoursColumns = useMemo<Column<OutstandingHoursItem>[]>(
    () => [
      {
        key: "kunde",
        header: "Kunde",
        render: (row) => row.kunde || "–",
      },
      {
        key: "project",
        header: "Projekt",
        render: (row) => (
          <ProjectOverviewLink
            auftragId={row.auftrag_id}
            label={row.auftragsnummer || "Projekt"}
            labelClassName="text-sm text-tint"
          />
        ),
      },
      {
        key: "timesheet",
        header: "Nachweis",
        render: (row) => timesheetLabel(row.timesheet_number, row.timesheet_id),
      },
      {
        key: "status",
        header: "Status",
        render: (row) => timesheetStatusLabel(row.status),
      },
      {
        key: "source",
        header: "Quelle",
        render: (row) =>
          row.source === "linked_mark" ? "Auf Nachweis verknüpft" : "Stundennachweis",
      },
      {
        key: "hours",
        header: "Stunden",
        className: "tabular-nums",
        render: (row) => formatHoursDe(row.hours),
      },
      {
        key: "rate",
        header: "Satz",
        className: "tabular-nums",
        render: (row) => formatEuro(row.unit_price),
      },
      {
        key: "amount",
        header: "Betrag",
        className: "tabular-nums font-medium",
        render: (row) =>
          row.amount == null ? (
            <span className="text-status-orange" title={row.warning || undefined}>
              {row.warning || "unbewertet"}
            </span>
          ) : (
            formatEuro(row.amount)
          ),
      },
    ],
    [],
  );

  const materialColumns = useMemo<Column<OutstandingMaterialItem>[]>(
    () => [
      {
        key: "kunde",
        header: "Kunde",
        render: (row) => row.kunde || "–",
      },
      {
        key: "project",
        header: "Projekt",
        render: (row) =>
          row.auftrag_id ? (
            <ProjectOverviewLink
              auftragId={row.auftrag_id}
              label={row.auftragsnummer || `Projekt ${row.auftrag_id}`}
              labelClassName="text-sm text-tint"
            />
          ) : (
            "–"
          ),
      },
      {
        key: "label",
        header: "Material",
        render: (row) => {
          const body = (
            <div>
              <div>{row.label}</div>
              {row.hero_nr ? (
                <div className="text-xs text-label-secondary">{row.hero_nr}</div>
              ) : null}
              {row.has_saved_price ? (
                <div className="text-xs text-label-secondary">kalkuliert</div>
              ) : null}
            </div>
          );
          if (!canKalkulateMaterial || row.id == null) return body;
          return (
            <button
              type="button"
              className="text-left text-tint hover:underline"
              onClick={() => setKalkMaterial(row)}
              title="Als Leistung kalkulieren"
            >
              {body}
            </button>
          );
        },
      },
      {
        key: "timesheet",
        header: "Nachweis",
        render: (row) => timesheetLabel(row.timesheet_number, row.timesheet_id),
      },
      {
        key: "status",
        header: "Status",
        render: (row) => timesheetStatusLabel(row.status),
      },
      {
        key: "qty",
        header: "Menge",
        className: "tabular-nums",
        render: (row) =>
          `${String(row.quantity).replace(".", ",")}${row.unit ? ` ${row.unit}` : ""}`,
      },
      {
        key: "rate",
        header: "VK",
        className: "tabular-nums",
        render: (row) => formatEuro(row.unit_price),
      },
      {
        key: "amount",
        header: "Betrag",
        className: "tabular-nums font-medium",
        render: (row) =>
          row.amount == null ? (
            <span className="text-status-orange">{row.warning || "unbewertet"}</span>
          ) : (
            formatEuro(row.amount)
          ),
      },
    ],
    [canKalkulateMaterial],
  );

  const travelColumns = useMemo<Column<OutstandingTravelItem>[]>(
    () => [
      {
        key: "kunde",
        header: "Kunde",
        render: (row) => row.kunde || "–",
      },
      {
        key: "timesheet",
        header: "Nachweis",
        render: (row) => timesheetLabel(row.timesheet_number, row.timesheet_id),
      },
      {
        key: "status",
        header: "Status",
        render: (row) => timesheetStatusLabel(row.status),
      },
      {
        key: "kind",
        header: "Art",
        render: (row) =>
          row.kind === "eighths" || row.unit === "Achtel" ? "Achtel" : "km",
      },
      {
        key: "qty",
        header: "Menge",
        className: "tabular-nums",
        render: (row) => {
          const unit =
            row.unit ||
            (row.kind === "eighths" ? "Achtel" : "km");
          return `${String(row.quantity).replace(".", ",")} ${unit}`;
        },
      },
      {
        key: "rate",
        header: "Satz",
        className: "tabular-nums",
        render: (row) => formatEuro(row.unit_price),
      },
      {
        key: "amount",
        header: "Betrag",
        className: "tabular-nums font-medium",
        render: (row) =>
          row.amount == null ? (
            <span className="text-status-orange">{row.warning || "unbewertet"}</span>
          ) : (
            formatEuro(row.amount)
          ),
      },
    ],
    [],
  );

  const ohneProjektColumns = useMemo<Column<OutstandingOpenHoursItem>[]>(
    () => [
      {
        key: "person",
        header: "Person",
        render: (row) => row.partner_name,
      },
      {
        key: "hours",
        header: "Stunden",
        className: "tabular-nums",
        render: (row) => formatHoursDe(row.hours),
      },
      {
        key: "rate",
        header: "Satz",
        className: "tabular-nums",
        render: () => formatEuro(openHourRate),
      },
      {
        key: "amount",
        header: "ca. Betrag",
        className: "tabular-nums font-medium",
        render: (row) => formatEuro(row.hours * openHourRate),
      },
    ],
    [openHourRate],
  );

  const ohneNachweisColumns = useMemo<Column<OutstandingOpenHoursItem>[]>(
    () => [
      {
        key: "kunde",
        header: "Kunde",
        render: (row) => row.kunde || "–",
      },
      {
        key: "project",
        header: "Projekt",
        render: (row) => (
          <ProjectOverviewLink
            auftragId={row.auftrag_id}
            label={row.projekt}
            labelClassName="text-sm text-tint"
          />
        ),
      },
      {
        key: "person",
        header: "Person",
        render: (row) => row.partner_name,
      },
      {
        key: "hours",
        header: "Stunden",
        className: "tabular-nums",
        render: (row) => formatHoursDe(row.hours),
      },
      {
        key: "rate",
        header: "Satz",
        className: "tabular-nums",
        render: (row) => formatEuro(row.unit_price),
      },
      {
        key: "amount",
        header: "ca. Betrag",
        className: "tabular-nums font-medium",
        render: (row) =>
          row.amount == null ? (
            <span className="text-status-orange" title={row.warning || undefined}>
              {row.warning || "unbewertet"}
            </span>
          ) : (
            formatEuro(row.amount)
          ),
      },
    ],
    [],
  );

  const isCurrentYear = year === currentYear();

  return (
    <div className="space-y-4">
      {error ? (
        <Alert variant="error" title="Außenstände konnten nicht geladen werden">
          {error}
        </Alert>
      ) : null}

      <section className="rounded-ios-lg bg-card px-4 py-4 shadow-ios-md ring-1 ring-separator/35">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-separator text-lg text-label transition hover:border-accent hover:text-accent"
              aria-label="Vorheriges Jahr"
              onClick={() => setYear((current) => shiftYear(current, -1))}
            >
              ‹
            </button>
            <div className="min-w-[5rem] text-center text-base font-semibold text-label">{year}</div>
            <button
              type="button"
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-separator text-lg text-label transition hover:border-accent hover:text-accent"
              aria-label="Nächstes Jahr"
              onClick={() => setYear((current) => shiftYear(current, 1))}
            >
              ›
            </button>
            {!isCurrentYear ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="h-8"
                onClick={() => setYear(currentYear())}
              >
                Aktuelles Jahr
              </Button>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            {refreshing ? (
              <span className="flex items-center gap-1.5 text-xs text-label-secondary">
                <Spinner className="h-3.5 w-3.5" />
                wird aktualisiert …
              </span>
            ) : null}
            <Button variant="secondary" size="sm" onClick={() => void load()} loading={loading}>
              Neu laden
            </Button>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-[1fr_auto]">
          <div className="flex flex-wrap items-end gap-3">
            <TextInput
              label="Stundensatz für Stunden ohne Projekt (€/h)"
              value={rateInput}
              inputMode="decimal"
              onChange={(e) => setRateInput(e.target.value)}
              onBlur={applyRate}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  applyRate();
                }
              }}
              className="min-w-[16rem] max-w-xs"
            />
            <Button type="button" size="sm" variant="secondary" onClick={applyRate}>
              Übernehmen
            </Button>
          </div>
          <div className="flex flex-col justify-end">
            <Checkbox
              label="Hero Lizenz Full ausblenden"
              checked={hideFullLicense}
              onChange={(e) => setHideFullLicense(e.target.checked)}
            />
          </div>
        </div>
        <p className="mt-3 text-xs text-label-secondary">
          Bewertung ungebuchter Stunden, Materialien sowie Fahrtkilometer bzw. Achtel mit
          Hero-VK bzw. Kunden-Stundensatz. Stunden ohne Projekt werden mit dem hier
          eingegebenen Satz geschätzt; Stunden ohne Nachweis mit dem Stundensatz des Kunden.
        </p>
      </section>

      {loading && !report ? (
        <div className="flex items-center justify-center py-16 text-label-secondary">
          <Spinner />
          <span className="ml-2 text-sm">Außenstände werden geladen …</span>
        </div>
      ) : (
        <>
          <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            <div className="rounded-ios-lg bg-card px-4 py-4 shadow-ios-md ring-1 ring-separator/35 sm:col-span-2 xl:col-span-1">
              <p className="text-xs text-label-secondary">Außenstände ca.</p>
              <p className="mt-1 text-3xl font-semibold tabular-nums text-label">
                {formatEuro(estimatedTotal)}
              </p>
            </div>
            <div className="rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35">
              <p className="text-xs text-label-secondary">Stunden (bewertet)</p>
              <p className="mt-1 text-xl font-semibold tabular-nums text-label">
                {formatEuro(summary?.hours_amount)}
              </p>
            </div>
            <div className="rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35">
              <p className="text-xs text-label-secondary">Materialien</p>
              <p className="mt-1 text-xl font-semibold tabular-nums text-label">
                {formatEuro(summary?.materials_amount)}
              </p>
            </div>
            <div className="rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35">
              <p className="text-xs text-label-secondary">Fahrt / Achtel</p>
              <p className="mt-1 text-xl font-semibold tabular-nums text-label">
                {formatEuro(summary?.travel_km_amount)}
              </p>
            </div>
            <div className="rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35">
              <p className="text-xs text-label-secondary">
                Ohne Projekt ({formatHoursDe(ohneProjektHours)})
              </p>
              <p className="mt-1 text-xl font-semibold tabular-nums text-label">
                {formatEuro(ohneProjektAmount)}
              </p>
            </div>
            <div className="rounded-ios-lg bg-card px-4 py-3 shadow-ios-md ring-1 ring-separator/35">
              <p className="text-xs text-label-secondary">
                Ohne Nachweis ({formatHoursDe(summary?.ohne_nachweis_hours)})
              </p>
              <p className="mt-1 text-xl font-semibold tabular-nums text-label">
                {formatEuro(ohneNachweisAmount)}
              </p>
            </div>
          </section>

          {(summary?.unpriced_hours_count ||
            summary?.unpriced_materials_count ||
            summary?.unpriced_travel_count ||
            summary?.unpriced_ohne_nachweis_count) ? (
            <Alert variant="warning" title="Unbewertete Positionen">
              {[
                summary?.unpriced_hours_count
                  ? `${summary.unpriced_hours_count} Stundenposition(en)`
                  : null,
                summary?.unpriced_materials_count
                  ? `${summary.unpriced_materials_count} Materialposition(en)`
                  : null,
                summary?.unpriced_travel_count
                  ? `${summary.unpriced_travel_count} Fahrt-/Achtel-Position(en)`
                  : null,
                summary?.unpriced_ohne_nachweis_count
                  ? `${summary.unpriced_ohne_nachweis_count} Stunden ohne Nachweis`
                  : null,
              ]
                .filter(Boolean)
                .join(" · ")}{" "}
              ohne Preis – fehlen in der bekannten Summe.
            </Alert>
          ) : null}

          <section className="space-y-2">
            <h2 className="text-sm font-semibold text-label">Stunden auf Nachweisen</h2>
            <Table
              columns={hoursColumns}
              rows={report?.hours ?? []}
              rowKey={(row) => `${row.source}:${row.timesheet_id}:${row.hours}`}
              emptyText="Keine ungebuchten Stunden auf Nachweisen."
            />
          </section>

          <section className="space-y-2">
            <h2 className="text-sm font-semibold text-label">Materialien</h2>
            <Table
              columns={materialColumns}
              rows={report?.materials ?? []}
              rowKey={(row) => String(row.id ?? `${row.timesheet_id}:${row.label}`)}
              emptyText="Keine Materialpositionen auf offenen Nachweisen."
            />
          </section>

          <section className="space-y-2">
            <h2 className="text-sm font-semibold text-label">Fahrtkilometer / Achtel</h2>
            <Table
              columns={travelColumns}
              rows={report?.travel_km ?? []}
              rowKey={(row) => String(row.timesheet_id)}
              emptyText="Keine Fahrtkilometer oder Achtel auf offenen Nachweisen."
            />
          </section>

          <section className="space-y-2">
            <h2 className="text-sm font-semibold text-label">Stunden ohne Projekt</h2>
            <Table
              columns={ohneProjektColumns}
              rows={report?.ohne_projekt ?? []}
              rowKey={(row) =>
                `op:${row.partner_name}:${row.seconds}:${row.hours}`
              }
              emptyText="Keine Stunden ohne Projekt."
            />
          </section>

          <section className="space-y-2">
            <h2 className="text-sm font-semibold text-label">Stunden ohne Nachweis</h2>
            <Table
              columns={ohneNachweisColumns}
              rows={report?.ohne_nachweis ?? []}
              rowKey={(row) =>
                `on:${row.auftrag_id ?? "x"}:${row.partner_name}:${row.projekt}`
              }
              emptyText="Keine Stunden ohne Nachweis."
            />
          </section>
        </>
      )}

      <OutstandingMaterialKalkDialog
        open={kalkMaterial != null}
        material={kalkMaterial}
        onClose={() => setKalkMaterial(null)}
        onSaved={() => void load({ background: true })}
      />
    </div>
  );
}
